/** Formatação pt-BR e mapeamento de estado para tinta visual. */
import type { Canal, EstadoMensagem, Kind, NivelLog, StatusConexao } from './types';

const numero = new Intl.NumberFormat('pt-BR');
const horaCurta = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'America/Cuiaba',
});
const horaLonga = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  timeZone: 'America/Cuiaba',
});
const dataHora = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  timeZone: 'America/Cuiaba',
});

export const n = (v: number | null | undefined) => numero.format(v ?? 0);
export const hhmm = (iso: string) => horaCurta.format(new Date(iso));
export const hhmmss = (iso: string) => horaLonga.format(new Date(iso));
export const ddmmHora = (iso: string) => dataHora.format(new Date(iso));

/** `6400` -> `6,4 s`. Zero vira travessão, como no design. */
export function segundos(ms: number | null | undefined): string {
  if (!ms) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} s`;
}

export function duracao(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return segundos(ms);
  return `${Math.round(ms / 60_000)} min`;
}

export function pct(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
}

/**
 * `+5565998124477` -> `+55 65 99812-4477`. Handles de bot e endereços de
 * e-mail passam intactos: o canal `email` usa o mesmo campo `peer`, e um
 * endereço como `08001234567@dominio.com` cairia na regra de 13 dígitos.
 */
export function telefone(bruto: string | null): string {
  if (!bruto) return '—';
  if (bruto.startsWith('@') || bruto.startsWith('http') || bruto.includes('@')) return bruto;

  const d = bruto.replace(/\D/g, '');
  if (d.length === 13) return `+${d.slice(0, 2)} ${d.slice(2, 4)} ${d.slice(4, 9)}-${d.slice(9)}`;
  if (d.length === 12) return `+${d.slice(0, 2)} ${d.slice(2, 4)} ${d.slice(4, 8)}-${d.slice(8)}`;
  return bruto;
}

// ------------------------------------------------------- estado -> tinta

export function kindDoStatus(status: StatusConexao): Kind {
  switch (status) {
    case 'online':
      return 'ok';
    case 'throttling':
    case 'pareando':
      return 'warn';
    case 'banido':
    case 'erro':
      return 'err';
    default:
      return 'mut';
  }
}

export const rotuloStatus: Record<StatusConexao, string> = {
  online: 'Online',
  throttling: 'Em throttling',
  offline: 'Offline',
  banido: 'Banido',
  pareando: 'Emparelhando',
  erro: 'Erro',
};

export function kindDoEstado(estado: EstadoMensagem): Kind {
  switch (estado) {
    case 'enviada':
    case 'entregue':
    case 'lida':
    case 'recebida':
      return 'ok';
    case 'retida':
    case 'fracionada':
      return 'warn';
    case 'falhou':
    case 'descartada':
      return 'err';
    default:
      return 'mut';
  }
}

export const rotuloEstado: Record<EstadoMensagem, string> = {
  queued: 'Na fila',
  retida: 'Retida',
  fracionada: 'Fracionada',
  enviando: 'Enviando',
  enviada: 'Enviada',
  entregue: 'Entregue',
  lida: 'Lida',
  falhou: 'Falhou',
  descartada: 'Descartada',
  recebida: 'Recebida',
};

export function kindDoNivel(nivel: NivelLog): Kind {
  switch (nivel) {
    case 'sucesso':
      return 'ok';
    case 'aviso':
      return 'warn';
    default:
      return 'err';
  }
}

export const rotuloNivel: Record<NivelLog, string> = {
  sucesso: 'Sucesso',
  aviso: 'Aviso',
  erro: 'Erro',
  banimento: 'Banimento',
};

export function kindDoContainer(status: string): Kind {
  switch (status) {
    case 'online':
      return 'ok';
    case 'sincronizando':
    case 'degradado':
      return 'warn';
    case 'offline':
      return 'err';
    default:
      return 'mut';
  }
}

/**
 * O que mostrar na coluna "Número autenticado" de cada canal.
 *
 * No formulário o `numero` é a URL do formulário público — quarenta caracteres
 * de uuid que estouravam a coluna e passavam por cima de Status e Fila. E a URL
 * inteira não ajuda ninguém ali: o link que importa é o da fila, que fica no
 * cadastro da conexão.
 */
export function identidadeDaConexao(canal: Canal, numero: string | null): string {
  if (!numero) return '—';
  if (canal === 'form') return 'Formulário publicado';
  if (canal === 'email') return numero;
  // O bot se identifica por @usuario, nao por telefone — passar pelo
  // formatador transformaria "@sga_bot" em lixo.
  if (canal === 'telegram') return numero;
  return telefone(numero);
}

export const iconeDoCanal: Record<Canal, { icone: string; cor: string }> = {
  openwa: { icone: 'mdi:whatsapp', cor: '#16a249' },
  evolution: { icone: 'mdi:api', cor: 'var(--sga-primary-text)' },
  telegram: { icone: 'mdi:robot-outline', cor: 'var(--sga-blue-3)' },
  // Icone cheio para a conta, contorno para o bot: na grade os dois aparecem
  // lado a lado, e o rotulo sozinho ("Telegram" e "Telegram (bot)") e pequeno
  // demais para distinguir de relance.
  telegram_conta: { icone: 'mdi:telegram', cor: 'var(--sga-blue-3)' },
  cloud: { icone: 'mdi:check-decagram', cor: 'var(--sga-primary-text)' },
  form: { icone: 'mdi:text-box-search-outline', cor: 'var(--sga-fg-2)' },
  email: { icone: 'mdi:email-outline', cor: 'var(--sga-blue-3)' },
};

export const iconeDoPerfilAntiban: Record<string, string> = {
  padrao: 'mdi:shield-check-outline',
  rigido: 'mdi:shield-alert-outline',
  aquecimento: 'mdi:shield-remove-outline',
  nao_aplica: 'mdi:shield-off-outline',
};
